import React from 'react';
import { Editor } from '@tiptap/react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link,
  Highlighter,
  Superscript,
  Subscript,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AIToolbar } from '@/components/notes/AIToolbar';

interface SimpleEditorToolbarProps {
  editor: Editor;
  className?: string;
  showAI?: boolean;
}

export function SimpleEditorToolbar({ editor, className, showAI = true }: SimpleEditorToolbarProps) {
  const formatButtons = [
    {
      icon: Bold,
      isActive: () => editor.isActive('bold'),
      onClick: () => editor.chain().focus().toggleBold().run(),
      disabled: () => !editor.can().chain().focus().toggleBold().run(),
      label: 'Bold',
    },
    {
      icon: Italic,
      isActive: () => editor.isActive('italic'),
      onClick: () => editor.chain().focus().toggleItalic().run(),
      disabled: () => !editor.can().chain().focus().toggleItalic().run(),
      label: 'Italic',
    },
    {
      icon: Underline,
      isActive: () => editor.isActive('underline'),
      onClick: () => editor.chain().focus().toggleUnderline().run(),
      disabled: () => !editor.can().chain().focus().toggleUnderline().run(),
      label: 'Underline',
    },
    {
      icon: Strikethrough,
      isActive: () => editor.isActive('strike'),
      onClick: () => editor.chain().focus().toggleStrike().run(),
      disabled: () => !editor.can().chain().focus().toggleStrike().run(),
      label: 'Strikethrough',
    },
    {
      icon: Code,
      isActive: () => editor.isActive('code'),
      onClick: () => editor.chain().focus().toggleCode().run(),
      disabled: () => !editor.can().chain().focus().toggleCode().run(),
      label: 'Code',
    },
  ];

  const headingButtons = [
    {
      icon: Heading1,
      isActive: () => editor.isActive('heading', { level: 1 }),
      onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      disabled: () => !editor.can().chain().focus().toggleHeading({ level: 1 }).run(),
      label: 'Heading 1',
    },
    {
      icon: Heading2,
      isActive: () => editor.isActive('heading', { level: 2 }),
      onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      disabled: () => !editor.can().chain().focus().toggleHeading({ level: 2 }).run(),
      label: 'Heading 2',
    },
    {
      icon: Heading3,
      isActive: () => editor.isActive('heading', { level: 3 }),
      onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      disabled: () => !editor.can().chain().focus().toggleHeading({ level: 3 }).run(),
      label: 'Heading 3',
    },
  ];

  const listButtons = [
    {
      icon: List,
      isActive: () => editor.isActive('bulletList'),
      onClick: () => editor.chain().focus().toggleBulletList().run(),
      disabled: () => !editor.can().chain().focus().toggleBulletList().run(),
      label: 'Bullet List',
    },
    {
      icon: ListOrdered,
      isActive: () => editor.isActive('orderedList'),
      onClick: () => editor.chain().focus().toggleOrderedList().run(),
      disabled: () => !editor.can().chain().focus().toggleOrderedList().run(),
      label: 'Numbered List',
    },
  ];

  const alignButtons = [
    {
      icon: AlignLeft,
      isActive: () => editor.isActive({ textAlign: 'left' }),
      onClick: () => editor.chain().focus().setTextAlign('left').run(),
      disabled: () => !editor.can().chain().focus().setTextAlign('left').run(),
      label: 'Align Left',
    },
    {
      icon: AlignCenter,
      isActive: () => editor.isActive({ textAlign: 'center' }),
      onClick: () => editor.chain().focus().setTextAlign('center').run(),
      disabled: () => !editor.can().chain().focus().setTextAlign('center').run(),
      label: 'Align Center',
    },
    {
      icon: AlignRight,
      isActive: () => editor.isActive({ textAlign: 'right' }),
      onClick: () => editor.chain().focus().setTextAlign('right').run(),
      disabled: () => !editor.can().chain().focus().setTextAlign('right').run(),
      label: 'Align Right',
    },
  ];

  const additionalButtons = [
    {
      icon: Quote,
      isActive: () => editor.isActive('blockquote'),
      onClick: () => editor.chain().focus().toggleBlockquote().run(),
      disabled: () => !editor.can().chain().focus().toggleBlockquote().run(),
      label: 'Quote',
    },
    {
      icon: Highlighter,
      isActive: () => editor.isActive('highlight'),
      onClick: () => editor.chain().focus().toggleHighlight().run(),
      disabled: () => !editor.can().chain().focus().toggleHighlight().run(),
      label: 'Highlight',
    },
    {
      icon: Superscript,
      isActive: () => editor.isActive('superscript'),
      onClick: () => editor.chain().focus().toggleSuperscript().run(),
      disabled: () => !editor.can().chain().focus().toggleSuperscript().run(),
      label: 'Superscript',
    },
    {
      icon: Subscript,
      isActive: () => editor.isActive('subscript'),
      onClick: () => editor.chain().focus().toggleSubscript().run(),
      disabled: () => !editor.can().chain().focus().toggleSubscript().run(),
      label: 'Subscript',
    },
  ];

  const historyButtons = [
    {
      icon: Undo,
      isActive: () => false,
      onClick: () => editor.chain().focus().undo().run(),
      disabled: () => !editor.can().chain().focus().undo().run(),
      label: 'Undo',
    },
    {
      icon: Redo,
      isActive: () => false,
      onClick: () => editor.chain().focus().redo().run(),
      disabled: () => !editor.can().chain().focus().redo().run(),
      label: 'Redo',
    },
  ];

  const renderToolbarGroup = (buttons: typeof formatButtons) => {
    return buttons.map((button, index) => {
      const Icon = button.icon;
      return (
        <Button
          key={index}
          variant={button.isActive() ? 'default' : 'ghost'}
          size="sm"
          onClick={button.onClick}
          disabled={button.disabled()}
          className={cn(
            'h-8 w-8 p-0',
            button.isActive() && 'bg-primary text-primary-foreground'
          )}
          title={button.label}
        >
          <Icon className="h-4 w-4" />
        </Button>
      );
    });
  };

  return (
    <div className={cn('flex items-center gap-1 p-2.5 flex-wrap shadow-sm', className)}>
      {/* Format buttons */}
      {renderToolbarGroup(formatButtons)}
      
      <Separator orientation="vertical" className="h-6 mx-1 bg-border/40" />
      
      {/* Heading buttons */}
      {renderToolbarGroup(headingButtons)}
      
      <Separator orientation="vertical" className="h-6 mx-1 bg-border/40" />
      
      {/* List buttons */}
      {renderToolbarGroup(listButtons)}
      
      <Separator orientation="vertical" className="h-6 mx-1 bg-border/40" />
      
      {/* Alignment buttons */}
      {renderToolbarGroup(alignButtons)}
      
      <Separator orientation="vertical" className="h-6 mx-1 bg-border/40" />
      
      {/* Additional formatting */}
      {renderToolbarGroup(additionalButtons)}
      
      <Separator orientation="vertical" className="h-6 mx-1 bg-border/40" />
      
      {/* History buttons */}
      {renderToolbarGroup(historyButtons)}
      
      {showAI && (
        <>
          <Separator orientation="vertical" className="h-6 mx-1 bg-border/40" />
          
          {/* AI Tools */}
          <AIToolbar editor={editor} />
        </>
      )}
    </div>
  );
}